import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnInit,
} from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import {
  DocumentSharingControllerService,
} from '../../../core/generated';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthCustomService } from '../../../security/auth-custom.service';
import { firstValueFrom } from 'rxjs';
import { ProfileProviderPJCacheListService } from '../../../shared/services/profile-provider-pj-cache-list.service';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-admin-document-share-details-dialog',
  standalone: true,
  imports: [MaterialModule],
  template: `
    <div class="modal-backdrop">
      <div class="modal-container">
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">Detalhes do Compartilhamento #{{ data.sharing.id }}</h3>
          </div>

          <mat-dialog-content class="modal-body">
            <div class="info-row">
              <span class="info-label">ID:</span>
              <span class="info-separator"></span>
              <span class="info-data">{{ data.sharing.id }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Proprietário:</span>
              <span class="info-separator"></span>
              <span class="info-data">{{ data.sharing.ownerPFEmail }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Empresa:</span>
              <span class="info-separator"></span>
              <span class="info-data">{{ data.companyName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Concedido em:</span>
              <span class="info-separator"></span>
              <span class="info-data">{{ data.sharing.grantedDate | date:'dd/MM/yyyy HH:mm' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Revogado em:</span>
              <span class="info-separator"></span>
              <span class="info-data">{{ data.sharing.revokedDate ? (data.sharing.revokedDate | date:'dd/MM/yyyy HH:mm') : '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Status:</span>
              <span class="info-separator"></span>
              <span class="badge" [ngClass]="data.sharing.active ? 'badge-success' : 'badge-error'">
                {{ data.sharing.active ? 'Ativo' : 'Revogado' }}
              </span>
            </div>
          </mat-dialog-content>

          <mat-dialog-actions class="modal-footer">
            <button mat-button class="btn btn-ghost" (click)="onClose()">
              <mat-icon>close</mat-icon> Fechar
            </button>
            @if (data.sharing.active) {
            <button mat-flat-button color="warn" class="btn btn-primary" (click)="onRevoke()">
              <mat-icon>block</mat-icon> Revogar Acesso
            </button>
            }
          </mat-dialog-actions>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .info-row {
      margin-bottom: var(--spacing-sm);
    }
  `]
})
export class AdminDocumentShareDetailsDialogComponent {
  dialogRef = inject(MatDialogRef<AdminDocumentShareDetailsDialogComponent>);

  constructor(@Inject(MAT_DIALOG_DATA) public data: { sharing: any; companyName: string }) {}

  onClose() {
    this.dialogRef.close();
  }

  onRevoke() {
    this.dialogRef.close({ action: 'revoke', id: this.data.sharing.id });
  }
}

@Component({
    selector: 'app-admin-document-share',
    imports: [MaterialModule, FormsModule],
    standalone: true,
    templateUrl: './admin-document-share.component.html',
    styleUrl: './admin-document-share.component.scss'
})
export class AdminDocumentShareComponent implements OnInit {
  authCustomService = inject(AuthCustomService);
  documentSharingService = inject(DocumentSharingControllerService); 
  profileProviderPJCacheListService = inject(ProfileProviderPJCacheListService);
  _snackBar = inject(MatSnackBar);
  cd = inject(ChangeDetectorRef);
  dialog = inject(MatDialog);

  documentSharing: any[] = [];
  allDocumentSharing: any[] = [];
  
  displayedColumnsAttachment: string[] = [
    'id',
    'ownerPFEmail',
    'grantedPJId',
    'grantedDate',
    'revokedDate',
    'active',
    'actions'
  ];

  // Filtros
  filterOwner: string = '';
  filterCompany: string = '';
  showOnlyActive: boolean = false;

  async ngOnInit() {
    await this.loadDocumentSharing();
  }

  async loadDocumentSharing() {
    const response = await firstValueFrom(
      this.documentSharingService.getAll()
    );
    if (response) {
      this.allDocumentSharing = response;
      this.documentSharing = [...this.allDocumentSharing];
      this.cd.detectChanges();
    }
  }

  async revokeAccess(id: number) {
    if (!confirm('Tem certeza que deseja revogar este acesso?')) {
      return;
    }

    try {
      await firstValueFrom(
        this.documentSharingService._delete(id)
      );
      
      this._snackBar.open('Acesso revogado com sucesso', 'Fechar', {
        duration: 3000,
      });
      
      await this.loadDocumentSharing();
    } catch (error) {
      this._snackBar.open('Erro ao revogar acesso', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao revogar acesso:', error);
    }
  }
  
  getCompanyName(grantedCompanyCnpj: string): string {
    const pj =
      this.profileProviderPJCacheListService.findProfileProviderPJByCnpj(
        grantedCompanyCnpj
      );
    return pj?.companyName ?? 'N/A';
  }

  viewDetails(sharing: any) {
    const companyName = this.getCompanyName(sharing.grantedPJId);
    
    const dialogRef = this.dialog.open(AdminDocumentShareDetailsDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { sharing, companyName }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && result.action === 'revoke') {
        await this.revokeAccess(result.id);
      }
    });
  }

  applyFilters() {
    let filtered = [...this.allDocumentSharing];

    if (this.filterOwner) {
      filtered = filtered.filter(item =>
        item.ownerPFEmail?.toLowerCase().includes(this.filterOwner.toLowerCase())
      );
    }

    if (this.filterCompany) {
      filtered = filtered.filter(item => {
        const companyName = this.getCompanyName(item.grantedPJId);
        return companyName.toLowerCase().includes(this.filterCompany.toLowerCase());
      });
    }

    if (this.showOnlyActive) {
      filtered = filtered.filter(item => item.active);
    }

    this.documentSharing = filtered;
    this.cd.detectChanges();
  }

  clearFilters() {
    this.filterOwner = '';
    this.filterCompany = '';
    this.showOnlyActive = false;
    this.documentSharing = [...this.allDocumentSharing];
    this.cd.detectChanges();
  }
}
